/* === BEGIN imported: modalPages/findingDriver.jsx (verbatim from plugin) === */
import React, {useState} from "react";
import {createPortal} from "react-dom";
import ModalHeader from "@/customComponents/uiComponents/modal/ModalHeader.jsx";
import {Loader} from "@/customComponents/uiComponents/Loader.jsx";
import {planeArrival, bigClose} from "@img/index.js";
// magnifier.svg and the terminal photos aren't in the @img barrel — the glob helper
// resolves anything in src/assets/images by name.
import {img} from "@/utils/pluginImg.js";
import {BOOKING_CONSTRAINTS} from "@/constants/booking";

// Guide-action glyphs. Inline (not @img assets) because these are the outlined
// style the design uses and they inherit the button's colour via `currentColor` —
// the shared icon files are solid #FCFCFC, drawn for dark buttons.
const PlayGlyph = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinejoin="round" aria-hidden="true">
        <path d="M8.5 5.4 18.5 12l-10 6.6z"/>
    </svg>
);

const DownloadGlyph = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 3.5v10.5"/>
        <path d="m7.5 9.5 4.5 4.5 4.5-4.5"/>
        <path d="M4.5 16.5v2a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-2"/>
    </svg>
);

const ShareGlyph = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="17.5" cy="5.5" r="2.6"/>
        <circle cx="6.5" cy="12" r="2.6"/>
        <circle cx="17.5" cy="18.5" r="2.6"/>
        <path d="m8.8 10.7 6.4-3.8"/>
        <path d="m8.8 13.3 6.4 3.8"/>
    </svg>
);



// Prefer the name the server advertises (Content-Disposition); fall back to the
// URL's last path segment.
const guideFileName = (res, url) => {
    const cd = res.headers.get('content-disposition') || '';
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
    if (match) {
        try {
            return decodeURIComponent(match[1]);
        } catch (_) {
            return match[1];
        }
    }
    try {
        return new URL(url, window.location.href).pathname.split('/').pop() || 'airport-guide';
    } catch (_) {
        return 'airport-guide';
    }
};

// Text that accompanies the guide in the share sheet.
const GUIDE_SHARE = {
    title: 'How to find your driver at the airport',
    text: "Here's how to find your driver at the airport",
};

/**
 * Terminal photo shown beside each step, in order. FILENAMES ONLY — the files live in
 * `src/assets/images/` and are resolved by the glob helper.
 *
 * Steps 1 and 3 share one shot (the pre-arranged doors) because they describe the same
 * place; step 2 is the gate "A" booth. An empty entry renders that step's copy across
 * the full row instead of reserving a hole, so a missing photo costs nothing.
 */
const STEP_PHOTOS = ['fd-step-doors.png', 'fd-step-gate-a.png', 'fd-step-doors.png'];

// The photo that sits at the right of a step, under a magnifier. It's a BUTTON: the
// magnifier promises a closer look, so tapping opens it full screen.
function StepThumb({src, onOpen}) {
    if (!src) return null;
    return (
        <button type="button" className="hopn-fd-step-thumb" onClick={onOpen} aria-label="View photo">
            <img decoding="async" src={img(src)} alt=""/>
            <span className="hopn-fd-step-zoom">
                <img src={img('magnifier.svg')} alt=""/>
            </span>
        </button>
    );
}

export function FindingDriver(props) {
    // Support number from /config/app (general.support_phone) — it was a hardcoded literal
    // in the step-3 tel: link until 2026-08-26. Mirrors the plugin's FindingDriver.
    // The terminal guide shows the local 10-digit number (no +1), dash-separated
    // (e.g. 416-458-2132); the tel: link keeps the full digits for dialling.
    const contactNumberTel = (BOOKING_CONSTRAINTS.CONTACT_NUMBER || '').replace(/\D/g, '');
    const contactNumberDisplay = contactNumberTel.slice(-10).replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
    const [copiedToast, setCopiedToast] = useState(false);
    // Share fetches the guide before the sheet can open — show the same spinner
    // Download uses while that is in flight.
    const [sharing, setSharing] = useState(false);
    // Which step's photo is open full screen; null = closed.
    const [photoIndex, setPhotoIndex] = useState(null);
    // React unmounts the instant state changes, so a fade-OUT needs the element kept
    // alive for the length of the animation: flag it closing, then drop it.
    const [photoClosing, setPhotoClosing] = useState(false);
    const closePhoto = () => {
        setPhotoClosing(true);
        setTimeout(() => {
            setPhotoIndex(null);
            setPhotoClosing(false);
        }, 200);
    };




    // DOWNLOAD goes through the booking portal's proxy instead
    // (App.php → wp_ajax_hopn_terminal_guide_download), which re-serves the guide
    // with `Content-Disposition: attachment`.
    //
    // Linking straight at docUrl is what made this open in a tab rather than save:
    // the guide is a plain .txt on another host, and the `download` attribute is
    // IGNORED cross-origin. The attachment header is not — it is the server's
    // instruction, so the browser saves the file whatever the origin. The plugin
    // reaches the same endpoint (same-origin there, so it can fetch → blob); here a
    // plain link is enough because the header does the forcing.
    const portalBase = (import.meta.env.VITE_BOOKING_PORTAL_URL || '').replace(/\/$/, '');
    const downloadUrl = portalBase
        ? `${portalBase}/wp-admin/admin-ajax.php?action=hopn_terminal_guide_download&t=1`
        : '';


    // Share the FILE itself rather than a link, so the recipient gets the guide in the
    // conversation instead of a URL to go and open (Web Share Level 2). That needs the
    // bytes, hence the fetch — the proxy sends Access-Control-Allow-Origin for exactly
    // this. Same URL the Download button uses: one source, so the two can never drift.
    //
    // Each branch RETURNS rather than falling through, because a share sheet the user
    // dismisses rejects the promise — indistinguishable from a failure — and falling
    // through would pop a second sheet at someone who just cancelled.
    const handleShare = async () => {
        if (!downloadUrl || sharing) return;

        setSharing(true);
        let file = null;
        try {
            const res = await fetch(downloadUrl);
            if (res.ok) {
                const blob = await res.blob();
                file = new File([blob], guideFileName(res, downloadUrl), {
                    type: blob.type || 'application/octet-stream',
                });
            }
        } catch (err) {
            // Almost always CORS: the guide host has to send Access-Control-Allow-Origin
            // for another origin to READ the bytes (the proxy does — once deployed).
            // Logged rather than swallowed, because the fallback below still shares a
            // link, so the file path failing is otherwise invisible.
            console.warn('[findingDriver] guide fetch failed — sharing the link instead', err);
        } finally {
            // Only the fetch is ours to wait on; the share sheet is the OS's and covers
            // the page anyway.
            setSharing(false);
        }

        // canShare({files}) is the only reliable capability test: `navigator.share`
        // exists on platforms that still reject file payloads.
        if (file && navigator.canShare?.({files: [file]})) {
            try {
                await navigator.share({...GUIDE_SHARE, files: [file]});
            } catch { /* sheet dismissed — nothing to recover from */ }
            return;
        }

        if (navigator.share) {
            try {
                await navigator.share({...GUIDE_SHARE, url: downloadUrl});
            } catch { /* sheet dismissed — nothing to recover from */ }
            return;
        }

        try {
            await navigator.clipboard.writeText(downloadUrl);
            setCopiedToast(true);
            setTimeout(() => setCopiedToast(false), 2000);
        } catch { /* clipboard blocked — no feedback to give */ }
    };

    return (
        <>
            {!props.hideHeader && (
                <ModalHeader
                    title=""
                    extraClasses="less-space"
                    totalSteps={0}
                    cancelButton={false}
                    backStep={props.previousStep || "bookingReceived"}
                    showBackButton={true}
                    setStep={props.setStep}
                    closeModal={props.closeModal}
                />
            )}


            {/* Copied Toast */}
            {copiedToast && (
                <div className="hopn-copied-toast">Link copied!</div>
            )}

            {/* Tapped photo, full screen. PORTALLED TO document.body — rendered in place
                it inherits the modal's width, its rounded clip and its stacking context,
                so it could only ever be a small panel inside the modal it opened from.
                Out here it owns the viewport: one photo, sized to fit it, and a single
                close button. Clicking anywhere but the photo closes it. */}
            {photoIndex !== null && createPortal(
                <div
                    className={`hopn-fd-photo-modal${photoClosing ? ' is-closing' : ''}`}
                    onClick={closePhoto}
                >
                    <button
                        type="button"
                        className="hopn-fd-photo-close"
                        onClick={closePhoto}
                        aria-label="Close"
                    >
                        <img src={bigClose} alt="Close"/>
                    </button>
                    <img
                        className="hopn-fd-photo-full"
                        src={img(STEP_PHOTOS[photoIndex])}
                        onClick={(e) => e.stopPropagation()}
                        alt=""
                    />
                </div>,
                document.body
            )}

            <div className="hopn-fd">
                {/* Mint hero: the context chip, the airport and its terminals. Same
                    stepped artwork as the lateness tabs — its bottom edge rises on the
                    right, and the guide actions below sit in the space that step leaves.
                    Set inline because the URL differs per portal. */}
                <div
                    className="hopn-fd-hero"
                    style={{backgroundImage: `url(${img('lateness-hero-bg.png')})`}}
                >
                    <span className="hopn-fd-chip">
                        {/* Arrival (landing) plane — this guide is for passengers who have
                            just landed, not departing ones. */}
                        <img decoding="async" src={planeArrival} alt=""/>
                        At the airport
                    </span>
                    <h3 className="hopn-fd-title">Toronto Pearson Airport</h3>
                    <span className="hopn-fd-terminals">Welcome to Terminal <b>1</b> or <b>3</b></span>
                </div>

                {/* Guide actions. The two arrivals terminals share one guide now, so this
                    is a single action group rather than a per-terminal bar. */}
                <div className="hopn-fd-actions-row">
                    <div className="hopn-fd-actions">
                        {/* Play is still an affordance only — the walkthrough video isn't
                            published yet, so it carries the "Coming soon" tooltip (same as
                            the plugin's guide). */}
                        <span className="hopn-fd-play-wrap">
                            <button type="button" className="hopn-fd-play" aria-label="Play">
                                <span className="hopn-fd-play-disc"><PlayGlyph/></span>
                            </button>
                            <span className="hopn-fd-tooltip">Coming soon</span>
                        </span>
                        {/* Points at the portal's attachment proxy (see downloadUrl), so the
                            browser saves the guide instead of rendering it. _blank keeps a
                            failed proxy (404 when the guide is unpublished, 502 when its host
                            is down) from replacing this page — a successful attachment never
                            navigates, so no stray tab is left behind. No href without a portal
                            URL, so it isn't clickable then. */}
                        <a
                            className={`hopn-fd-btn${downloadUrl ? '' : ' is-disabled'}`}
                            href={downloadUrl || undefined}
                            download
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Download"
                            aria-disabled={!downloadUrl}
                        >
                            <DownloadGlyph/>
                        </a>
                        {/* Same busy treatment as Download: the share sheet only opens
                            once the guide has been fetched, so without feedback the button
                            reads as dead and repeat taps queue more fetches. */}
                        <button
                            type="button"
                            className={`hopn-fd-btn${sharing ? ' is-busy' : ''}`}
                            onClick={handleShare}
                            disabled={!downloadUrl || sharing}
                            aria-label="Share"
                        >
                            {sharing ? <Loader height={12} width={2} margin={1}/> : <ShareGlyph/>}
                        </button>
                    </div>
                </div>

                <div className="hopn-fd-card">
                    <ol className="hopn-fd-steps">
                        <li>
                            <span className="hopn-fd-step-no">1-</span>
                            <div className="hopn-fd-step-body">
                                <div className="hopn-fd-step-text">
                                    <strong>Clear Immigration, Customs &amp; Retrieve Luggage</strong>
                                    <span>Follow pre-arranged limo area signage</span>
                                    <span>after picking luggage</span>
                                </div>
                                <StepThumb src={STEP_PHOTOS[0]} onOpen={() => setPhotoIndex(0)}/>
                            </div>
                        </li>
                        <li>
                            <span className="hopn-fd-step-no">2-</span>
                            <div className="hopn-fd-step-body">
                                <div className="hopn-fd-step-text">
                                    <strong>Meet Driver</strong>
                                    <span className="hopn-fd-step-lead">Head to Pickup Point</span>
                                    <span>Locate gate "A" at the pre-arranged limo booth</span>
                                </div>
                                <StepThumb src={STEP_PHOTOS[1]} onOpen={() => setPhotoIndex(1)}/>
                            </div>
                        </li>
                        <li>
                            <span className="hopn-fd-step-no">3-</span>
                            <div className="hopn-fd-step-body">
                                <div className="hopn-fd-step-text">
                                    <strong>Provide Your Name</strong>
                                    <span>Provide your name to the official at the booth</span>
                                    <span>or call us at <a href={`tel:${contactNumberTel}`}>{contactNumberDisplay}</a></span>
                                </div>
                                <StepThumb src={STEP_PHOTOS[2]} onOpen={() => setPhotoIndex(2)}/>
                            </div>
                        </li>
                    </ol>

                    <p className="hopn-fd-note">
                        No need to go outside the terminal building; stay inside in the heated/air-conditioned room.
                        Your driver will be ready to pick you up in just a few minutes.
                        <b>Safe travels!</b>
                    </p>
                </div>
            </div>
        </>
    );
}
/* === END imported: modalPages/findingDriver.jsx === */
